import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarNoCarDepency } from "../../http/cars/types";
import { Cost } from "../../http/costs/types";
import { useCostTypesApi } from "../../http/costTypes/api";
import { CostType } from "../../http/costTypes/types";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const COST_TYPES_FIELDS = ["id", "name"] as const;
type CostTypeField = (typeof COST_TYPES_FIELDS)[number];
type CostTypeSearch = Pick<CostType, CostTypeField>;

export interface CostFormBody {
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  } | null;
  type: CostType;
  date: string;
  costNet: number | null;
  costGross: number | null;
}

type CostFormProps = {
  cost?: Cost | null;
  onSubmit: (form: CostFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  allowSelectCar?: boolean;
};

const CostForm: FC<CostFormProps> = ({ cost, onSubmit, isDisabled, isLoading, allowSelectCar }: CostFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const costTypesApi = useCostTypesApi();
  const aborter = useRef(new AbortController());

  const types = costTypesApi.useAdminCostTypes({ limit: 100, fields: COST_TYPES_FIELDS }, aborter.current.signal);

  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);

  const defaultValues = {
    car: cost?.car,
    type: cost?.type,
    date: cost?.date,
    costNet: cost?.costNet,
    costGross: cost?.costGross,
  };

  return (
    <Form<CostFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={cost}
    >
      {({ control, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [cost]);

        return (
          <FormFieldsContainer>
            {!!allowSelectCar && (
              <Controller
                name="car"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          isDisabled={!!cost || isDisabled || isLoading}
                          isLoading={isLoading}
                          inputId={labelId}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<{
                            id: string;
                            vehicleIdentificationNumber: string;
                            currentPlateNumber?: string;
                            lastPlateNumber?: string;
                          }>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.costTypes.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      isLoading={isLoading}
                      isDisabled={isLoading || isDisabled}
                      inputId={labelId}
                      options={types.data?.items}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<CostTypeSearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="date"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  const d = new Date(value!);
                  if (d.getFullYear() < 1000) {
                    return i18n.t.common.invalidDate;
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.date} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => !!v && setValue("date", v)}
                      value={watch("date")}
                      disabled={isDisabled || isLoading}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="costNet"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.costNet} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isLoading || isDisabled}
                      onBlur={(value) => setValue("costNet", value ?? null)}
                      decimalScale={2}
                      suffix=" €"
                      allowNegative={false}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="costGross"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.costGross} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isLoading || isDisabled}
                      onBlur={(value) => setValue("costGross", value ?? null)}
                      decimalScale={2}
                      suffix=" €"
                      allowNegative={false}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CostForm;
